import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "../../../components/Button";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { brand } from "../../../theme/theme";
import { formatNoun, getFormatRule } from "./platformMeta";

const PRESETS = [
  { label: "1:1", aspect: 1 },
  { label: "4:5", aspect: 4 / 5 },
  { label: "1,91:1", aspect: 1.91 },
  { label: "9:16", aspect: 9 / 16 },
];

const fullCrop = { unit: "%", x: 0, y: 0, width: 100, height: 100 };

const cropFor = (aspect, width, height) =>
  aspect ? centerCrop(makeAspectCrop({ unit: "%", width: 100 }, aspect, width, height), width, height) : fullCrop;

/** Percent crop → whole pixels of the image, pulled inside the format's limits (rounding can step over 1.91). */
const toBox = (crop, image, { min, max }) => {
  const x = Math.round((crop.x / 100) * image.width);
  const y = Math.round((crop.y / 100) * image.height);
  let width = Math.min(Math.round((crop.width / 100) * image.width), image.width - x);
  let height = Math.min(Math.round((crop.height / 100) * image.height), image.height - y);
  if (max && width / height > max) width = Math.floor(height * max);
  if (min && width / height < min) height = Math.floor(width / min);
  return { x, y, width, height };
};

/** Picks the crop box of an image (pixels); `onSave(box)` crops it and throws on error (the dialog stays open). */
export function ImageCropDialog({ image, platform, format, onClose, onSave }) {
  const { minImageAspect, maxImageAspect } = getFormatRule(platform, format);
  const limits = { min: minImageAspect, max: maxImageAspect };
  const imageAspect = image.width / image.height;
  // Starts on the closest shape the network takes.
  const initialAspect =
    limits.max && imageAspect > limits.max ? limits.max : limits.min && imageAspect < limits.min ? limits.min : null;
  const [crop, setCrop] = useState(() => cropFor(initialAspect, image.width, image.height));
  const [saving, setSaving] = useState(false);

  // Only the shapes the format takes. A preset just places the selection: resizing it stays free.
  const presets = PRESETS.filter(({ aspect: value }) => (!limits.max || value <= limits.max) && (!limits.min || value >= limits.min));

  const box = toBox(crop, image, limits);
  const unchanged = box.x === 0 && box.y === 0 && box.width === image.width && box.height === image.height;
  const selectedRatio = (crop.width * image.width) / (crop.height * image.height || 1);
  const matchesPreset = (value) => Math.abs(selectedRatio - value) < 0.01;
  const outOfLimits =
    (limits.max && selectedRatio > limits.max + 0.005) || (limits.min && selectedRatio < limits.min - 0.005);

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(box);
      onClose();
    } catch {
      setSaving(false);
    }
  };

  return (
    <UnifiedModal
      open
      onClose={() => !saving && onClose()}
      title="Recortar imagen"
      maxWidth="md"
      footer={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          <Typography sx={{ flex: 1, fontSize: "0.75rem", color: "#6B7280" }}>
            {box.width} × {box.height} px
          </Typography>
          <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" disabled={saving || unchanged || box.width < 1 || box.height < 1} onClick={submit}>
            {saving ? "Recortando..." : "Recortar"}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }} role="radiogroup" aria-label="Proporción">
        {presets.map(({ label, aspect: value }) => {
          const selected = matchesPreset(value);
          return (
            <Box
              key={label}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={saving}
              onClick={() => setCrop(cropFor(value, image.width, image.height))}
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: "999px",
                border: `1px solid ${selected ? brand.pink : "#E5E7EB"}`,
                backgroundColor: selected ? brand.soft : "#fff",
                color: selected ? brand.pink : "#374151",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", backgroundColor: "#F3F4F6", borderRadius: "12px", p: 1 }}>
        <ReactCrop
          crop={crop}
          onChange={(_pixels, percent) => setCrop(percent)}
          keepSelection
          disabled={saving}
          // On ReactCrop, not the img: its CSS sets the img to max-height: inherit. 90vh modal minus header, presets and footer.
          style={{ maxHeight: "calc(90vh - 300px)" }}
        >
          <img src={image.image} alt="" />
        </ReactCrop>
      </Box>

      {outOfLimits && (
        <Typography sx={{ mt: 1.25, fontSize: "0.8125rem", color: "#B91C1C" }}>
          {formatNoun(platform, format)} no admite esta proporción: al recortar se ajustará al límite permitido.
        </Typography>
      )}
    </UnifiedModal>
  );
}

ImageCropDialog.propTypes = {
  image: PropTypes.shape({
    image: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  platform: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
